/**
 * FE-side mirror of the BE `snap()` function in
 * `3mote-be-new/src/time/time-service.ts`. Pure helper used by every
 * surface that wants to preview the rounding consequence BEFORE the
 * round-trip — modals, DnD ghosts, the settings example line.
 *
 * The BE remains the canonical guard (defense-in-depth — direct API
 * callers, MCP tools, and future agent flows aren't trusted to
 * pre-snap correctly). This helper exists purely to keep the FE
 * display honest about what the BE will store.
 *
 * The snap math runs in epoch ms. Every minute increment we expose
 * (5, 6, 10, 15, 30, 60) divides 30 evenly, so even half-hour
 * timezone offsets (IST = UTC+5:30, Newfoundland = UTC-3:30) land on
 * the correct local minute boundary after the round-trip.
 */

import type { TimeRoundingDirection, TimeRoundingMinutes } from '@/features/tenant-settings/types'

/**
 * Snap a Date to a minute boundary using the org's rounding config.
 *
 * - `minutes === 0` is a no-op (returns a clone of the input).
 * - `direction === 'up'` always advances to the next boundary (no-op
 *   when already on one).
 * - `direction === 'down'` always retreats to the previous boundary
 *   (no-op when already on one).
 * - `direction === 'nearest'` is round-half-up: remainder >= half
 *   rounds up, anything less rounds down.
 *
 * Seconds + sub-second precision are dropped on the snapped output
 * (matches the BE), except in the `minutes === 0` pass-through where
 * the input is preserved verbatim.
 */
export function snapDate(
  d: Date,
  minutes: TimeRoundingMinutes,
  direction: TimeRoundingDirection,
): Date {
  if (minutes === 0) return new Date(d.getTime())
  const ms = d.getTime()
  const stepMs = minutes * 60 * 1000
  const remainder = ms % stepMs

  let snappedMs: number
  if (remainder === 0) {
    snappedMs = ms
  } else if (direction === 'up') {
    snappedMs = ms + (stepMs - remainder)
  } else if (direction === 'down') {
    snappedMs = ms - remainder
  } else {
    const half = stepMs / 2
    snappedMs = remainder < half ? ms - remainder : ms + (stepMs - remainder)
  }
  const snapped = new Date(snappedMs)
  snapped.setUTCSeconds(0, 0)
  return snapped
}

/**
 * True when applying `snapDate` to the input would produce a
 * different Date value. Use to gate the "Will be stored as …" preview
 * hint so the line is hidden when the user happens to enter a value
 * that already lands on the grid.
 */
export function snapWouldChange(
  d: Date,
  minutes: TimeRoundingMinutes,
  direction: TimeRoundingDirection,
): boolean {
  if (minutes === 0) return false
  const snapped = snapDate(d, minutes, direction)
  return snapped.getTime() !== d.getTime()
}
